// Phase 6 — Track C T-C3. /login route.
//
// Centred card layout (Container + Surface + Card + Stack from primitives)
// with the DailyDeck wordmark, sign-in copy, email/password fields, and a
// primary submit button. Submits via `apiClient.auth.login({ mode: 'cookie' })`.
//
// Outcome handling (per plan §2.5 / Track C T-C3):
//   * 200 → invalidate AUTH_ME_QUERY_KEY (in case any future hook reads
//     it via react-query) AND call `signIn(user)` on the AuthContext so
//     downstream guards see the new state without a network round-trip.
//     Then navigate to ?from path or `/`.
//   * 401 (or `invalid_credentials` code) → render
//     `copy.errors.invalid_credentials` inline below the form.
//   * Network / 5xx → render `copy.errors.network_unavailable`.
//
// All interactive elements are keyboard-reachable and the focus ring is
// inherited from the Input/Button primitives.

import * as React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, AuthRequiredError, NetworkError } from '@dailydeck/shared/api';
import { copy } from '@dailydeck/shared/copy';
import { useAuth, AUTH_ME_QUERY_KEY } from '../../app/AuthContext.js';
import { apiClient } from '../../app/apiClient.js';
import { loginFormSchema } from './loginSchema.js';
import { Container } from '../../components/ui/Container.js';
import { Card, CardContent } from '../../components/ui/Card.js';
import { Stack } from '../../components/ui/Stack.js';
import { Input } from '../../components/ui/Input.js';
import { Button } from '../../components/ui/Button.js';

function GoogleIcon(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.08 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-3.59-13.46-8.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

interface FieldErrors {
  email?: string;
  password?: string;
}

function safeFromParam(search: string): string {
  const params = new URLSearchParams(search);
  const raw = params.get('from');
  if (raw === null) return '/';
  // Defence in depth — only allow same-origin paths so a malicious
  // `?from=https://evil` link cannot redirect post-login.
  if (!raw.startsWith('/') || raw.startsWith('//')) return '/';
  if (raw.startsWith('/login')) return '/';
  return raw;
}

export function Login(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { signIn, status } = useAuth();

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [fieldErrors, setFieldErrors] = React.useState<FieldErrors>({});
  const [formError, setFormError] = React.useState<string | null>(null);

  React.useEffect(() => {
    document.title = 'DailyDeck — Sign in';
  }, []);

  // If the user is already authenticated, send them straight to ?from or /.
  // Avoids the awkward "I just logged in, why am I still on /login?" state
  // that arises if someone navigates here manually.
  React.useEffect(() => {
    if (status === 'authenticated') {
      navigate(safeFromParam(location.search), { replace: true });
    }
  }, [status, navigate, location.search]);

  // Read ?error= query param set by the backend OAuth callback redirect and
  // surface the appropriate inline error message.
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const err = params.get('error');
    if (err === null) return;
    if (err === 'google_cancelled') setFormError(copy.auth.googleErrorCancelled);
    else if (err === 'google_state') setFormError(copy.auth.googleErrorState);
    else if (err === 'google_failed') setFormError(copy.auth.googleErrorFailed);
    else if (err === 'google_noemail') setFormError(copy.auth.googleErrorNoEmail);
  }, [location.search]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setFormError(null);
    setFieldErrors({});

    const parsed = loginFormSchema.safeParse({ email, password });
    if (!parsed.success) {
      const flat = parsed.error.flatten();
      const next: FieldErrors = {};
      const emailErr = flat.fieldErrors.email?.[0];
      const passErr = flat.fieldErrors.password?.[0];
      if (emailErr !== undefined) next.email = emailErr;
      if (passErr !== undefined) next.password = passErr;
      setFieldErrors(next);
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiClient.auth.login({
        email: parsed.data.email,
        password: parsed.data.password,
        mode: 'cookie',
      });
      signIn(res.user);
      await queryClient.invalidateQueries({ queryKey: [...AUTH_ME_QUERY_KEY] });
      navigate(safeFromParam(location.search), { replace: true });
    } catch (err) {
      if (err instanceof AuthRequiredError) {
        setFormError(copy.errors.invalid_credentials);
      } else if (err instanceof NetworkError) {
        setFormError(copy.errors.network_unavailable);
      } else if (err instanceof ApiError && err.code === 'invalid_credentials') {
        setFormError(copy.errors.invalid_credentials);
      } else if (err instanceof ApiError && err.status >= 500) {
        setFormError(copy.errors.server_error);
      } else if (err instanceof ApiError) {
        setFormError(err.message || copy.errors.unknown);
      } else {
        setFormError(copy.errors.unknown);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-base text-text-primary flex items-center justify-center px-4 py-12">
      <Container width="sm">
        <Card>
          <CardContent className="p-8 pt-8">
            <Stack gap={6}>
              <Stack gap={2} align="center">
                <span className="text-title-2 font-semibold tracking-tight">DailyDeck</span>
                <h1 className="text-title-3 font-semibold">{copy.auth.signInTitle}</h1>
                <p className="text-caption text-text-secondary">{copy.auth.signInSubtitle}</p>
              </Stack>

              {/* Google login */}
              <a
                href="/api/auth/google/login"
                className="flex items-center justify-center gap-3 w-full border border-border-subtle rounded-lg px-4 py-2.5 text-sm font-medium text-text-primary bg-surface-base hover:bg-surface-raised transition-colors"
                aria-label={copy.auth.continueWithGoogle}
              >
                <GoogleIcon />
                {copy.auth.continueWithGoogle}
              </a>

              {/* "or" divider */}
              <div className="flex items-center gap-3" aria-hidden="true">
                <div className="flex-1 h-px bg-border-subtle" />
                <span className="text-caption text-text-tertiary">or</span>
                <div className="flex-1 h-px bg-border-subtle" />
              </div>

              <form onSubmit={handleSubmit} noValidate aria-describedby={formError ? 'login-form-error' : undefined}>
                <Stack gap={4}>
                  <div>
                    <label htmlFor="login-email" className="text-caption text-text-secondary block mb-1">
                      {copy.auth.emailLabel}
                    </label>
                    <Input
                      id="login-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      placeholder={copy.auth.emailPlaceholder}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      aria-invalid={fieldErrors.email !== undefined}
                      aria-describedby={fieldErrors.email !== undefined ? 'login-email-error' : undefined}
                      required
                      disabled={submitting}
                    />
                    {fieldErrors.email !== undefined && (
                      <p id="login-email-error" role="alert" className="mt-1 text-caption text-state-danger">
                        {fieldErrors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="login-password" className="text-caption text-text-secondary block mb-1">
                      {copy.auth.passwordLabel}
                    </label>
                    <Input
                      id="login-password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      aria-invalid={fieldErrors.password !== undefined}
                      aria-describedby={fieldErrors.password !== undefined ? 'login-password-error' : undefined}
                      required
                      disabled={submitting}
                    />
                    {fieldErrors.password !== undefined && (
                      <p id="login-password-error" role="alert" className="mt-1 text-caption text-state-danger">
                        {fieldErrors.password}
                      </p>
                    )}
                  </div>

                  {formError !== null && (
                    <p
                      id="login-form-error"
                      role="alert"
                      className="text-caption text-state-danger"
                    >
                      {formError}
                    </p>
                  )}

                  <Button type="submit" variant="primary" disabled={submitting}>
                    {submitting ? copy.auth.signingIn : copy.auth.signInSubmit}
                  </Button>

                  <div className="text-center">
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      onClick={() => {
                        // Phase 7 ships forgot-password. Stub for v1.
                      }}
                      disabled={submitting}
                    >
                      Forgot password?
                    </Button>
                  </div>

                  {/* Phase 7.1 — point users at /register. */}
                  <div className="text-center text-caption text-text-secondary">
                    {copy.auth.register.linkToRegisterPrefix}{' '}
                    <Link to="/register" className="text-text-primary underline underline-offset-2">
                      {copy.auth.register.linkToRegisterCta}
                    </Link>
                  </div>
                </Stack>
              </form>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </div>
  );
}
